import { useJsApiLoader } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];
const GOOGLE_MAPS_API_KEY = 'AIzaSyBfROJsmdp_wGan1h6lRe3H9C5bSCtQZxo';

interface PlaceResult {
  address: string;
  latitude: number;
  longitude: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (result: PlaceResult) => void;
  placeholder?: string;
  label?: string;
  types?: string[];
  countryCode?: string;
  locationBias?: { lat: number; lng: number };
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 50px;
  padding: 10px 12px 10px 36px;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #1f2937;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: #E07B2D;
  }

  &:hover {
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
`;

const SuggestionItem = styled.li`
  padding: 10px 12px;
  font-size: 13px;
  color: #1f2937;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.1s ease;
  list-style: none;

  &:hover {
    background: #f3f4f6;
  }
`;

export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Wpisz adres...',
  label,
  types,
  countryCode,
  locationBias,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dummyDiv = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
      if (!dummyDiv.current) {
        dummyDiv.current = document.createElement('div');
      }
      placesService.current = new google.maps.places.PlacesService(dummyDiv.current);
    }
  }, [isLoaded]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback((input: string) => {
    if (!autocompleteService.current || input.length < 3) {
      setSuggestions([]);
      return;
    }

    const request: google.maps.places.AutocompletionRequest = {
      input,
      ...(types && types.length > 0 ? { types } : {}),
      ...(countryCode ? { componentRestrictions: { country: countryCode } } : {}),
      ...(locationBias ? {
        locationBias: new google.maps.Circle({
          center: locationBias,
          radius: 30000,
        }),
      } : {}),
    };

    autocompleteService.current.getPlacePredictions(
      request,
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      },
    );
  }, [types, countryCode, locationBias]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    fetchSuggestions(val);
  };

  const handleSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    const mainText = prediction.structured_formatting?.main_text || prediction.description;
    onChange(mainText);
    setShowSuggestions(false);
    setSuggestions([]);

    if (placesService.current && onPlaceSelect) {
      placesService.current.getDetails(
        { placeId: prediction.place_id, fields: ['geometry', 'formatted_address'] },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            onPlaceSelect({
              address: place.formatted_address || prediction.description,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            });
          }
        },
      );
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      {label && <Label>{label}</Label>}
      <InputWrapper>
        <IconWrapper>
          <MapPin size={16} />
        </IconWrapper>
        <StyledInput
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
      </InputWrapper>
      {showSuggestions && suggestions.length > 0 && (
        <SuggestionsList>
          {suggestions.map((s) => (
            <SuggestionItem key={s.place_id} onClick={() => handleSelect(s)}>
              {s.description}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
    </Wrapper>
  );
};
